// Commitment detail page — FE-043 / FE-043b.
//
// Renders the full Commitment surface plus the action controls
// BE-052 exposes (status transition, soft delete, block / unblock,
// assignment, reviewer set). FE-043b replaces the "click Edit, go
// to a separate form" flow with ClickUp-style inline editing —
// every field can be entered into edit mode in place. The /edit
// route still exists as a deep link, but the detail page no longer
// surfaces an Edit button as the primary affordance.

import { ArrowLeft, Check, Plus, Sparkles } from "lucide-react";
import { useContext, useState } from "react";

import { BlockNoteEditor } from "@/components/blocknote";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Link, useLocation, useNavigate, useParams,
} from "react-router-dom";

import { AuthContext } from "@/features/auth/AuthContext";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { CommitmentAttachmentsPanel } from "./CommitmentAttachmentsPanel";
import { useCommitmentAttachmentsQuery } from "./hooks";
import { CommitmentDetailRightPanel } from "./CommitmentDetailRightPanel";
import { CommitmentStatusBadge, statusLabel } from "./CommitmentStatusBadge";
import { COMMITMENT_STATUSES_ORDERED } from "./commitmentStatusMeta";
import {
  canMakePrivate,
  COMMITMENT_VISIBILITY_OPTIONS,
  PRIVATE_BLOCK_COPY,
  privateReassignmentNeedsConfirmation,
  reviewerCollidesWithAssignee,
} from "./commitmentVisibility";
import { CreateTagModal } from "./CreateTagModal";
import { DependencyPickerModal } from "./DependencyPickerModal";
import { PrivateVisibilityConfirmModal } from "./PrivateVisibilityConfirmModal";
import { InlineEditableField } from "./InlineEditableField";
import { InlineFieldRow } from "./InlineFieldRow";
import { SearchableSelect } from "./SearchableSelect";
import { describeTransitionError } from "./transitionErrorCopy";
import {
  useAccountsPickerQuery,
  useAddCommitmentDependencyMutation,
  useArchiveCommitmentMutation,
  useAssignCommitmentMutation,
  useBlockCommitmentMutation,
  useCategoriesPickerQuery,
  useCommitmentDetailQuery,
  useCreateCommitmentMutation,
  useCreateTagMutation,
  useDeleteCommitmentMutation,
  useMembersPickerQuery,
  usePlanYearPickerQuery,
  useRemoveCommitmentDependencyMutation,
  useRestoreCommitmentMutation,
  useReviewCommitmentMutation,
  useTagCommitmentMutation,
  useTagsPickerQuery,
  useTransitionCommitmentMutation,
  useUnblockCommitmentMutation,
  useUntagCommitmentMutation,
  useUnwatchCommitmentMutation,
  useUpdateCommitmentMutation,
  useWatchCommitmentMutation,
} from "./hooks";
import {
  CommitmentNotFoundError,
  type CommitmentDetail,
  type CommitmentFormVisibility,
  type CommitmentStatus,
} from "./types";


/** Location state stamp that the Tracker pages set when linking
 * into a Commitment so the back link can return to the original
 * filtered URL instead of the bare ``/commitment-tracker`` route.
 * Optional — direct links and bookmarks still work. */
export interface CommitmentDetailLocationState {
  fromUrl?: string;
  fromLabel?: string;
}


export function CommitmentDetailPage() {
  const params = useParams();
  const commitmentId = params.commitmentId ?? "";
  const navigate = useNavigate();
  const detailQuery = useCommitmentDetailQuery(commitmentId);

  if (detailQuery.isLoading || detailQuery.isPending) {
    return (
      <div data-testid="commitment-detail-page" className="flex flex-col gap-5">
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  if (detailQuery.isError) {
    if (detailQuery.error instanceof CommitmentNotFoundError) {
      return (
        <div
          data-testid="commitment-detail-not-found"
          className="flex flex-col gap-5"
        >
          <DashboardEmptyState
            title="Commitment not found"
            description="It may have been deleted, or the URL is wrong."
            action={
              <Link
                to="/dashboard/commitment-tracker"
                className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
              >
                Back to Commitment Tracker
              </Link>
            }
          />
        </div>
      );
    }
    return (
      <div data-testid="commitment-detail-page" className="flex flex-col gap-5">
        <DashboardErrorState
          title="Couldn't load this Commitment"
          description="Refresh to retry, or check the network connection."
          retry={() => detailQuery.refetch()}
        />
      </div>
    );
  }

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <div data-testid="commitment-detail-page" className="flex flex-col gap-5">
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  return (
    <DetailBody
      detail={detail}
      onDeleted={() =>
        navigate("/dashboard/commitment-tracker", { replace: true })
      }
    />
  );
}


export interface CommitmentDetailContentProps {
  detail: CommitmentDetail;
  /** Called after a successful soft-delete. Page mode navigates
   * away; modal mode closes the modal and falls back to the
   * tracker. */
  onDeleted: () => void;
  /** ``page`` (default) renders the full-page chrome — the Back
   * link above the two-column layout, plus the page-style
   * ``data-testid``. ``modal`` skips the back link (the modal
   * header carries the close affordance) and stamps a
   * modal-specific testid so tests can disambiguate. */
  chrome?: "page" | "modal";
}


/** Public Commitment detail body. Rendered by both the full-page
 * route (``CommitmentDetailPage``) and the route-overlay modal
 * (``CommitmentDetailModal``) so the modal-first surface and the
 * direct-URL fallback render the exact same logic. The component
 * owns its own mutations + auth gating; callers only supply the
 * loaded ``detail`` payload and the ``onDeleted`` redirect hook. */
export function CommitmentDetailContent(props: CommitmentDetailContentProps) {
  return <DetailBody {...props} />;
}


interface DetailBodyProps extends CommitmentDetailContentProps {}


function DetailBody({ detail, onDeleted, chrome = "page" }: DetailBodyProps) {
  const transitionMutation = useTransitionCommitmentMutation(
    detail.commitment_id,
  );
  const deleteMutation = useDeleteCommitmentMutation(detail.commitment_id);
  const archiveMutation = useArchiveCommitmentMutation(detail.commitment_id);
  const restoreMutation = useRestoreCommitmentMutation(detail.commitment_id);
  const blockMutation = useBlockCommitmentMutation(detail.commitment_id);
  const unblockMutation = useUnblockCommitmentMutation(detail.commitment_id);
  const updateMutation = useUpdateCommitmentMutation();
  const assignMutation = useAssignCommitmentMutation();
  const membersQuery = useMembersPickerQuery();
  const tagsQuery = useTagsPickerQuery();
  const accountsQuery = useAccountsPickerQuery();
  const categoriesQuery = useCategoriesPickerQuery();
  // Plan-year picker is account-scoped; only fires when an
  // Account is set on the Commitment.
  const planYearsQuery = usePlanYearPickerQuery(
    detail.account_id ?? detail.client_id ?? null,
  );
  const tagMutation = useTagCommitmentMutation();
  const untagMutation = useUntagCommitmentMutation();
  const watchMutation = useWatchCommitmentMutation();
  const unwatchMutation = useUnwatchCommitmentMutation();
  const reviewMutation = useReviewCommitmentMutation();
  const addDependencyMutation = useAddCommitmentDependencyMutation();
  const removeDependencyMutation = useRemoveCommitmentDependencyMutation();
  const createMutation = useCreateCommitmentMutation();
  // Used by the Account picker to warn the broker before changing
  // the Account on a Commitment that already has attachments — the
  // file links stay pointing at the old Account's documents
  // (per FE-048's "Account Documents is the source of truth"
  // contract) and won't auto-rewire to the new Account's cabinet.
  const attachmentsQuery = useCommitmentAttachmentsQuery(
    detail.commitment_id,
  );
  // Auth context — used to gate the Approve checkmark on the
  // Reviewer row. Read directly so this page mounts in tests
  // without an AuthProvider (falls back to ``null``).
  const auth = useContext(AuthContext);

  const [pendingCancel, setPendingCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState<string | null>(
    null,
  );
  const [pendingBlock, setPendingBlock] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [blockReasonError, setBlockReasonError] = useState<string | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState(false);
  const [fieldError, setFieldError] = useState<
    Record<string, string | null>
  >({});
  const [dependencyPickerOpen, setDependencyPickerOpen] = useState(false);
  const [subtaskDraft, setSubtaskDraft] = useState("");
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  const [createTagOpen, setCreateTagOpen] = useState(false);
  const createTagMutation = useCreateTagMutation();
  // Confirm modal for the "you're about to take someone else's
  // Commitment private" flow (safety rule: you cannot take someone
  // else's work private without an explicit yes).
  const [privateConfirmOpen, setPrivateConfirmOpen] = useState(false);
  // Set to true after we auto-clear the reviewer because an assignee
  // change would have collided with it. Drives a helper note next to
  // the reviewer picker so the user understands what just happened.
  const [reviewerAutoCleared, setReviewerAutoCleared] = useState(false);
  // Account-change confirmation: when the broker swaps the Account
  // on a Commitment that already has attachments, we ask them to
  // confirm via the branded ConfirmDialog. The picker closes
  // immediately on pick (fire-and-forget) — the dialog just gates
  // whether we run the actual patch or do nothing.
  const [accountChangeConfirm, setAccountChangeConfirm] = useState<
    | {
        nextAccountId: string | null;
        attachmentCount: number;
      }
    | null
  >(null);

  const location = useLocation();
  const state = location.state as CommitmentDetailLocationState | null;
  const backUrl = state?.fromUrl ?? "/dashboard/commitment-tracker";
  const backLabel = state?.fromLabel ?? "Back to Commitment Tracker";

  function patch(
    field: string,
    payload: Parameters<typeof updateMutation.mutate>[0]["values"],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      updateMutation.mutate(
        { commitment_id: detail.commitment_id, values: payload },
        {
          onSuccess: () => {
            setFieldError((prev) => ({ ...prev, [field]: null }));
            resolve();
          },
          onError: (err) => {
            setFieldError((prev) => ({
              ...prev,
              [field]:
                describeTransitionError(err)
                ?? "Couldn't save that change.",
            }));
            reject(err);
          },
        },
      );
    });
  }

  function handleTransition(status: CommitmentStatus): void {
    if (status === "cancelled") {
      setPendingCancel(true);
      setCancelReason("");
      setCancelReasonError(null);
      return;
    }
    transitionMutation.mutate({ to_status: status });
  }

  function submitCancellation(): void {
    if (cancelReason.trim() === "") {
      setCancelReasonError("Cancellation reason is required.");
      return;
    }
    setCancelReasonError(null);
    transitionMutation.mutate(
      { to_status: "cancelled", cancellation_reason: cancelReason.trim() },
      {
        onSuccess: () => {
          setPendingCancel(false);
          setCancelReason("");
        },
      },
    );
  }

  function submitBlock(): void {
    if (blockReason.trim() === "") {
      setBlockReasonError("Reason is required.");
      return;
    }
    setBlockReasonError(null);
    blockMutation.mutate(
      { blocked_reason: blockReason.trim() },
      {
        onSuccess: () => {
          setPendingBlock(false);
          setBlockReason("");
        },
      },
    );
  }

  const transitionError = describeTransitionError(transitionMutation.error);

  const dueLocal = detail.due_date_time
    ? toDatetimeLocal(detail.due_date_time)
    : "";

  const currentAssignee = detail.assigned_to ?? "";
  const assigneeDisplay = detail.assigned_to_display_name
    ?? detail.assigned_to_email
    ?? detail.assigned_job_title
    ?? "Unassigned";

  return (
    <div
      data-testid={
        chrome === "modal"
          ? "commitment-detail-modal-content"
          : "commitment-detail-page"
      }
      className="flex min-h-0 min-w-0 flex-col gap-3 lg:h-full"
    >
      {/* Back link sits above the two-column row so the main card
          and the Comments/Activity rail start at the same Y. The
          modal chrome hides this row — the modal header already
          carries the close X / Escape affordance, so an extra back
          link would just clutter the surface. */}
      {chrome !== "modal" && (
        <Link
          to={backUrl}
          data-testid="commitment-detail-back"
          className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={12} aria-hidden="true" />
          {backLabel}
        </Link>
      )}

      <div
        // ``lg:items-stretch`` makes the right rail match the main
        // column's height. ``lg:h-full`` + ``min-h-0`` lets the
        // panel anchor to the page so its compose row pins to the
        // bottom (matching the v1 layout).
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-4"
      >
        {/* Main column — the full Commitment surface. Right rail
            (Comments / Activity) lives in the sibling aside; layout
            collapses to single column below ``lg``. */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 lg:overflow-y-auto">
          <article className="flex flex-col gap-5 rounded-lg border border-border-subtle bg-raised p-6">
          {/* Header: title + commitment number + delete CTA. The
              old "Edit" button is gone in FE-043b — every field
              is click-to-edit. Delete still pops a confirm
              modal lower in the tree. */}
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
                {detail.commitment_number}
                {detail.archived_at && (
                  <span
                    data-testid="commitment-detail-archived-badge"
                    className="inline-flex items-center rounded-full bg-recessed px-2 py-0.5 text-[10px] font-semibold text-text-secondary"
                    title="This Commitment is archived"
                  >
                    Archived
                  </span>
                )}
              </span>
              {detail.parent_commitment_id && (
                <Link
                  to={`/dashboard/commitments/${detail.parent_commitment_id}`}
                  data-testid="commitment-detail-parent-link"
                  className="inline-flex w-fit items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
                >
                  <span className="text-text-tertiary">Subtask of</span>
                  <span className="font-medium underline decoration-border-subtle underline-offset-2">
                    {detail.parent_commitment_title ?? "parent"}
                  </span>
                </Link>
              )}
              <InlineEditableField
                label=""
                fieldId="title"
                mode="text"
                value={detail.title}
                display={(
                  <h1 className="break-words text-xl font-semibold tracking-tight text-text-primary">
                    {detail.title}
                  </h1>
                )}
                // Title editor lifts to the same typography as the
                // rendered heading so the input doesn't look tiny
                // next to the long title text. ``w-full`` pairs with
                // the parent's ``min-w-0`` so the input grows to the
                // full column width instead of inheriting the
                // intrinsic width of the trigger button.
                inputClassName="w-full text-xl font-semibold tracking-tight"
                isPending={updateMutation.isPending}
                error={fieldError.title}
                onSave={(next) => patch("title", { title: next })}
              />
              <span
                data-testid="commitment-detail-created-line"
                className="text-xs italic text-text-tertiary"
              >
                Created on {detail.created_at
                  ? formatDateTime(detail.created_at)
                  : "—"}
                {(detail.created_by_display_name
                  || detail.created_by_email) && (
                  <>
                    {" by "}
                    {detail.created_by_display_name
                      || detail.created_by_email}
                  </>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {detail.archived_at ? (
                <button
                  type="button"
                  data-testid="commitment-detail-restore"
                  onClick={() => restoreMutation.mutate()}
                  disabled={restoreMutation.isPending}
                  className="rounded-md border border-teal/40 bg-teal-tint/30 px-3 py-1.5 text-sm font-medium text-teal hover:bg-teal-tint disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Restore
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="commitment-detail-archive"
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isPending}
                  className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Archive
                </button>
              )}
              <button
                type="button"
                data-testid="commitment-detail-delete"
                onClick={() => setPendingDelete(true)}
                className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-ember hover:bg-ember-tint"
              >
                Delete
              </button>
            </div>
          </header>

          {/* Two-column metadata grid — matches the Pencil
              ``Commitment Detail`` frame. Each row is a label on
              the left and a click-to-edit value on the right. */}
          <section
            data-testid="commitment-detail-fields"
            className="grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2"
          >
            <div className="flex flex-col gap-3">
              <DetailGridRow label="Status" testId="status">
                <SearchableSelect
                  fieldId="status"
                  value={detail.status}
                  options={COMMITMENT_STATUSES_ORDERED.map((s) => ({
                    value: s,
                    label: statusLabel(s),
                  }))}
                  triggerDisplay={<CommitmentStatusBadge status={detail.status} />}
                  isPending={transitionMutation.isPending}
                  onPick={(next) => {
                    if (next === "cancelled") {
                      handleTransition("cancelled");
                      return Promise.resolve();
                    }
                    return new Promise<void>((resolve, reject) => {
                      transitionMutation.mutate(
                        { to_status: next as CommitmentStatus },
                        {
                          onSuccess: () => {
                            setFieldError((p) => ({ ...p, status: null }));
                            resolve();
                          },
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              status: describeTransitionError(err)
                                ?? "Couldn't change status.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    });
                  }}
                />
                {fieldError.status && (
                  <p
                    role="alert"
                    data-testid="detail-row-status-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.status}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Blocked" testId="blocked">
                <div className="flex flex-col gap-2">
                  <span
                    className="flex items-center gap-2"
                    title={
                      detail.is_blocked && detail.blocked_reason
                        ? `Reason: ${detail.blocked_reason}`
                        : undefined
                    }
                  >
                    <BlockedToggle
                      isBlocked={detail.is_blocked}
                      pendingBlock={pendingBlock}
                      onBlockClick={() => setPendingBlock(true)}
                      onUnblockClick={() => unblockMutation.mutate()}
                      pending={blockMutation.isPending || unblockMutation.isPending}
                    />
                    {detail.is_blocked && detail.blocked_reason && (
                      <span
                        data-testid="commitment-detail-blocked-reason"
                        className="truncate text-xs italic text-text-secondary"
                      >
                        — {detail.blocked_reason}
                      </span>
                    )}
                  </span>
                  {pendingBlock && (
                    <div className="flex flex-col gap-2 rounded-md bg-recessed/60 p-2">
                      <input
                        type="text"
                        autoFocus
                        id="commitment-detail-block-reason"
                        data-testid="commitment-detail-block-reason"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="Why blocked?"
                        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
                      />
                      {blockReasonError && (
                        <span className="text-[11px] font-medium text-ember">
                          {blockReasonError}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid="commitment-detail-block-submit"
                          onClick={submitBlock}
                          className="rounded-md bg-gold px-2 py-0.5 text-[11px] font-medium text-text-inverse hover:opacity-90"
                        >
                          Block
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPendingBlock(false);
                            setBlockReason("");
                            setBlockReasonError(null);
                          }}
                          className="rounded-md px-2 py-0.5 text-[11px] text-text-secondary hover:text-text-primary"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </DetailGridRow>
              <InlineFieldRow
                label="Due Date"
                fieldId="due"
                mode="datetime"
                value={dueLocal}
                display={
                  detail.due_date_time
                    ? <span className="text-teal underline">{formatDateTime(detail.due_date_time)}</span>
                    : <span className="text-text-tertiary">Click to set due date</span>
                }
                isPending={updateMutation.isPending}
                error={fieldError.due}
                onSave={(next) =>
                  patch("due", {
                    due_date_time: next ? new Date(next).toISOString() : null,
                  })
                }
              />
              <DetailGridRow label="Complexity" testId="complexity">
                <SearchableSelect
                  fieldId="complexity"
                  value={detail.complexity != null ? String(detail.complexity) : ""}
                  options={[
                    { value: "", label: "Unsized", isClear: true },
                    { value: "1", label: "Small (1 pt)" },
                    { value: "2", label: "Medium (2 pts)" },
                    { value: "3", label: "Large (3 pts)" },
                    { value: "5", label: "X-Large (5 pts)" },
                  ]}
                  triggerDisplay={<ComplexityChip complexity={detail.complexity} />}
                  isPending={updateMutation.isPending}
                  onPick={(next) =>
                    patch("complexity", {
                      complexity: next ? Number(next) : undefined,
                    })
                  }
                />
              </DetailGridRow>
              <DetailGridRow label="Category" testId="category">
                <SearchableSelect
                  fieldId="category"
                  value={detail.category_slug}
                  options={[
                    { value: "", label: "Uncategorized", isClear: true },
                    ...((categoriesQuery.data?.categories ?? []).map((c) => ({
                      value: c.slug,
                      label: c.display_name,
                    }))),
                  ]}
                  triggerDisplay={
                    detail.category_name
                      ? <span>{detail.category_name}</span>
                      : <span className="italic text-text-tertiary">Click to set category</span>
                  }
                  isPending={updateMutation.isPending}
                  onPick={(next) =>
                    patch("category", { category_slug: next })
                  }
                />
                {fieldError.category && (
                  <p
                    role="alert"
                    data-testid="detail-row-category-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.category}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Priority" testId="priority">
                <SearchableSelect
                  fieldId="priority"
                  value={detail.priority}
                  options={[
                    { value: "low", label: "Low" },
                    { value: "medium", label: "Medium" },
                    { value: "high", label: "High" },
                  ]}
                  triggerDisplay={<span className="text-teal">{priorityLabel(detail.priority)}</span>}
                  isPending={updateMutation.isPending}
                  onPick={(next) =>
                    patch("priority", {
                      priority: next as CommitmentDetail["priority"],
                    })
                  }
                />
              </DetailGridRow>
              {(detail.status === "done" || detail.completed_at) && (
                <DetailGridRow label="Completed At" testId="completed_at">
                  <span className="text-sm text-text-secondary">
                    {detail.completed_at
                      ? formatDateTime(detail.completed_at)
                      : "—"}
                  </span>
                </DetailGridRow>
              )}
              <DetailGridRow label="Watchers" testId="watchers">
                <SearchableSelect
                  fieldId="watchers"
                  multiple
                  value={detail.watchers.map((w) => w.user_id)}
                  options={(membersQuery.data?.members ?? []).map((m) => ({
                    value: m.id,
                    label: m.display_name || m.email,
                    hint: m.email,
                  }))}
                  triggerDisplay={
                    detail.watchers.length === 0
                      ? <span className="italic text-text-tertiary">Click to add watchers</span>
                      : (
                        <span className="flex flex-wrap items-center gap-1">
                          {detail.watchers.slice(0, 3).map((w) => (
                            <span
                              key={w.user_id}
                              className="inline-flex items-center gap-1 rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary"
                            >
                              {w.display_name ?? w.email ?? w.user_id}
                            </span>
                          ))}
                          {detail.watchers.length > 3 && (
                            <span className="text-[11px] text-text-tertiary">
                              +{detail.watchers.length - 3}
                            </span>
                          )}
                        </span>
                      )
                  }
                  onPick={(userId) =>
                    new Promise<void>((resolve, reject) => {
                      watchMutation.mutate(
                        { commitment_id: detail.commitment_id, user_id: userId },
                        {
                          onSuccess: () => resolve(),
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              watchers: describeTransitionError(err)
                                ?? "Couldn't add watcher.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    })
                  }
                  onUnpick={(userId) =>
                    new Promise<void>((resolve, reject) => {
                      unwatchMutation.mutate(
                        { commitment_id: detail.commitment_id, user_id: userId },
                        {
                          onSuccess: () => resolve(),
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              watchers: describeTransitionError(err)
                                ?? "Couldn't remove watcher.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    })
                  }
                  isPending={
                    watchMutation.isPending || unwatchMutation.isPending
                  }
                />
                {fieldError.watchers && (
                  <p
                    role="alert"
                    data-testid="detail-row-watchers-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.watchers}
                  </p>
                )}
              </DetailGridRow>
            </div>

            <div className="flex flex-col gap-3">
              <DetailGridRow label="Account" testId="account">
                <div className="flex flex-col gap-1">
                  <SearchableSelect
                    fieldId="account"
                    value={detail.account_id ?? detail.client_id ?? ""}
                    options={[
                      { value: "", label: "No account", isClear: true },
                      ...((accountsQuery.data?.accounts ?? []).map((a) => ({
                        value: a.id,
                        label: a.legal_name,
                        hint: a.dba_name ?? undefined,
                      }))),
                    ]}
                    triggerDisplay={
                      detail.account_name
                        ? <span className="text-teal underline">{detail.account_name}</span>
                        : <span className="italic text-text-tertiary">Click to set Account</span>
                    }
                    isPending={updateMutation.isPending}
                    onPick={(accountId) => {
                      const currentAccountId = (
                        detail.account_id ?? detail.client_id ?? null
                      );
                      const nextAccountId = accountId || null;
                      const attachmentCount = (
                        attachmentsQuery.data?.rows.length ?? 0
                      );
                      // Account-change prompt: attachments stay
                      // linked to the old Account's documents until
                      // the broker explicitly unlinks them. Open the
                      // branded ConfirmDialog and defer the actual
                      // patch until the broker confirms; on cancel we
                      // do nothing, so the Account field stays as it
                      // was. The picker doesn't gate close on this
                      // promise (fire-and-forget), so resolving with
                      // ``Promise.resolve()`` is safe.
                      if (
                        currentAccountId !== nextAccountId
                        && attachmentCount > 0
                      ) {
                        setAccountChangeConfirm({
                          nextAccountId, attachmentCount,
                        });
                        return Promise.resolve();
                      }
                      return patch(
                        "account", { client_id: nextAccountId },
                      );
                    }}
                  />
                  {detail.account_id && (
                    <Link
                      to={`/dashboard/accounts/${detail.account_id}`}
                      data-testid="commitment-detail-account-link"
                      className="ml-2 inline-flex w-fit text-[11px] text-text-tertiary hover:text-text-primary"
                    >
                      Open Account ↗
                    </Link>
                  )}
                  {fieldError.account && (
                    <p
                      role="alert"
                      data-testid="detail-row-account-error"
                      className="mt-1 text-[11px] font-medium text-ember"
                    >
                      {fieldError.account}
                    </p>
                  )}
                </div>
              </DetailGridRow>
              <DetailGridRow label="Assigned To" testId="assignee">
                <SearchableSelect
                  fieldId="assignee"
                  value={currentAssignee}
                  // Private Commitments are pinned to the actor — the
                  // BE rejects reassignment, so lock the picker on the
                  // FE too instead of letting users start a flow that
                  // will only fail at PATCH time.
                  disabled={detail.visibility === "private"}
                  options={[
                    { value: "", label: "Unassigned", isClear: true },
                    ...((membersQuery.data?.members ?? []).map((m) => ({
                      value: m.id,
                      label: m.display_name || m.email,
                      hint: m.email,
                    }))),
                  ]}
                  triggerDisplay={
                    <AssigneeAvatar
                      initials={initialsOf(assigneeDisplay)}
                      display={assigneeDisplay}
                    />
                  }
                  isPending={assignMutation.isPending}
                  onPick={(userId) => {
                    // Team-visible: BE rejects assignee == reviewer. If
                    // the picked assignee would collide with the
                    // current reviewer, clear the reviewer first so
                    // the BE accepts the assign call. The PATCH and
                    // assign fire independently — both are tiny.
                    const willCollide = reviewerCollidesWithAssignee({
                      assignedTo: userId,
                      reviewerId: detail.reviewer_id,
                    });
                    if (willCollide) {
                      setReviewerAutoCleared(true);
                      void patch("reviewer", { reviewer_id: null })
                        .catch(() => {});
                    }
                    return new Promise<void>((resolve, reject) => {
                      assignMutation.mutate(
                        {
                          commitment_id: detail.commitment_id,
                          input: {
                            assigned_to: userId || null,
                            assigned_job_title: null,
                            assigned_client_contact_id: null,
                          },
                        },
                        {
                          onSuccess: () => {
                            setFieldError((p) => ({ ...p, assignee: null }));
                            resolve();
                          },
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              assignee:
                                describeTransitionError(err)
                                ?? "Couldn't update assignee.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    });
                  }}
                />
                {detail.visibility === "private" && (
                  <p
                    data-testid="detail-row-assignee-locked-helper"
                    className="mt-1 text-[11px] text-text-tertiary"
                  >
                    Locked while Private — switch to Team-visible to reassign.
                  </p>
                )}
                {fieldError.assignee && (
                  <p
                    role="alert"
                    data-testid="detail-row-assignee-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.assignee}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Reviewer" testId="reviewer">
                <div className="flex items-center gap-2">
                  <SearchableSelect
                    fieldId="reviewer"
                    value={detail.reviewer_id ?? ""}
                    // Private rows have no reviewer — the BE rejects
                    // any reviewer_id PATCH while Private. Lock the
                    // picker so users don't start a flow that will
                    // only fail server-side.
                    disabled={detail.visibility === "private"}
                    options={[
                      { value: "", label: "No reviewer", isClear: true },
                      ...((membersQuery.data?.members ?? [])
                        // Team-visible: hide the current assignee — the
                        // BE rejects assignee == reviewer with
                        // "Reviewer must be different from assignee."
                        .filter((m) => !detail.assigned_to
                          || m.id !== detail.assigned_to)
                        .map((m) => ({
                          value: m.id,
                          label: m.display_name || m.email,
                          hint: m.email,
                        }))),
                    ]}
                    triggerDisplay={
                      detail.reviewer_display_name
                        ? <span>{detail.reviewer_display_name}</span>
                        : detail.reviewer_email
                          ? <span>{detail.reviewer_email}</span>
                          : <span className="italic text-text-tertiary">Click to set reviewer</span>
                    }
                    isPending={updateMutation.isPending}
                    onPick={(userId) => {
                      // Manual reviewer change clears the auto-cleared
                      // helper note — the user has now explicitly
                      // chosen a reviewer (or "No reviewer").
                      setReviewerAutoCleared(false);
                      return patch(
                        "reviewer", { reviewer_id: userId || null },
                      );
                    }}
                  />
                  {/* Approve / unapprove button — only visible when
                      the current user IS the assigned reviewer. The
                      check icon flips between empty (pending review)
                      and filled teal (reviewed). v3.19 — review is
                      decoupled from the done transition. */}
                  {auth?.user?.id
                    && detail.reviewer_id
                    && detail.reviewer_id === auth.user.id && (
                    <button
                      type="button"
                      data-testid="commitment-detail-review-toggle"
                      title={
                        detail.reviewed_at
                          ? "Reviewed — click to revoke"
                          : "Mark reviewed"
                      }
                      disabled={reviewMutation.isPending}
                      onClick={() =>
                        reviewMutation.mutate({
                          commitment_id: detail.commitment_id,
                          reviewed: !detail.reviewed_at,
                        })
                      }
                      className={[
                        "inline-flex h-6 w-6 items-center justify-center rounded-full transition-fast ease-out",
                        detail.reviewed_at
                          ? "bg-teal text-text-inverse"
                          : "border border-border-subtle text-text-tertiary hover:border-teal hover:text-teal",
                        "disabled:cursor-not-allowed disabled:opacity-60",
                      ].join(" ")}
                      aria-pressed={Boolean(detail.reviewed_at)}
                    >
                      <Check size={12} aria-hidden="true" />
                    </button>
                  )}
                  {detail.reviewed_at
                    && (!auth?.user?.id || detail.reviewer_id !== auth.user.id)
                    && (
                      <span
                        data-testid="commitment-detail-reviewed-badge"
                        title="Reviewed"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal text-text-inverse"
                      >
                        <Check size={10} aria-hidden="true" />
                      </span>
                    )}
                </div>
                {detail.visibility === "private" && (
                  <p
                    data-testid="detail-row-reviewer-locked-helper"
                    className="mt-1 text-[11px] text-text-tertiary"
                  >
                    Private Commitments cannot have reviewers.
                  </p>
                )}
                {reviewerAutoCleared
                  && detail.visibility !== "private" && (
                  <p
                    data-testid="detail-row-reviewer-cleared-helper"
                    className="mt-1 text-[11px] text-text-tertiary"
                  >
                    Reviewer cleared — they now match the assignee.
                  </p>
                )}
                {fieldError.reviewer && (
                  <p
                    role="alert"
                    data-testid="detail-row-reviewer-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.reviewer}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Tags" testId="tags">
                <SearchableSelect
                  fieldId="tags"
                  multiple
                  value={detail.tags
                    .map((t) => t.id ?? t.tag_id ?? "")
                    .filter((s): s is string => Boolean(s))}
                  options={(tagsQuery.data?.tags ?? []).map((t) => ({
                    value: t.id,
                    label: t.display_name,
                    hint: t.is_regulatory ? "Regulatory" : t.slug,
                    leading: (
                      <span
                        aria-hidden="true"
                        className={[
                          "inline-block h-2 w-2 rounded-full",
                          t.is_regulatory ? "bg-ember" : "bg-teal/60",
                        ].join(" ")}
                      />
                    ),
                  }))}
                  triggerDisplay={
                    detail.tags.length === 0
                      ? <span className="italic text-text-tertiary">Click to add tags</span>
                      : (
                        <span className="flex flex-wrap items-center gap-1">
                          {detail.tags.map((tag) => (
                            <span
                              key={tag.id ?? tag.tag_id ?? tag.slug ?? tag.name}
                              className={[
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
                                tag.is_regulatory
                                  ? "bg-ember-tint text-ember"
                                  : "bg-recessed text-text-secondary",
                              ].join(" ")}
                            >
                              {tag.display_name ?? tag.name}
                            </span>
                          ))}
                        </span>
                      )
                  }
                  onPick={(tagId) =>
                    new Promise<void>((resolve, reject) => {
                      tagMutation.mutate(
                        {
                          commitment_id: detail.commitment_id,
                          tag_id: tagId,
                        },
                        {
                          onSuccess: () => resolve(),
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              tags: describeTransitionError(err)
                                ?? "Couldn't add tag.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    })
                  }
                  onUnpick={(tagId) =>
                    new Promise<void>((resolve, reject) => {
                      untagMutation.mutate(
                        {
                          commitment_id: detail.commitment_id,
                          tag_id: tagId,
                        },
                        {
                          onSuccess: () => resolve(),
                          onError: (err) => {
                            setFieldError((p) => ({
                              ...p,
                              tags: describeTransitionError(err)
                                ?? "Couldn't remove tag.",
                            }));
                            reject(err);
                          },
                        },
                      );
                    })
                  }
                  isPending={
                    tagMutation.isPending || untagMutation.isPending
                  }
                />
                <button
                  type="button"
                  data-testid="commitment-detail-create-tag"
                  onClick={() => setCreateTagOpen(true)}
                  className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-teal hover:underline"
                >
                  <Plus size={10} aria-hidden="true" />
                  Create tag
                </button>
                {fieldError.tags && (
                  <p
                    role="alert"
                    data-testid="detail-row-tags-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.tags}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Plan Year" testId="plan_year">
                <SearchableSelect
                  fieldId="plan_year"
                  value={detail.plan_year_id ?? ""}
                  options={[
                    { value: "", label: "No plan year", isClear: true },
                    ...((planYearsQuery.data?.plan_years ?? []).map((py) => ({
                      value: py.id,
                      label: formatPlanYearRange(
                        py.effective_start, py.effective_end,
                      ),
                      hint: py.is_current ? "Current" : undefined,
                    }))),
                  ]}
                  triggerDisplay={
                    detail.plan_year_id
                      ? <span className="text-teal">{formatPlanYearRange(detail.plan_year_start, detail.plan_year_end)}</span>
                      : <span className="italic text-text-tertiary">Click to set plan year</span>
                  }
                  isPending={updateMutation.isPending}
                  onPick={(planYearId) =>
                    patch("plan_year", { plan_year_id: planYearId || null })
                  }
                />
                {fieldError.plan_year && (
                  <p
                    role="alert"
                    data-testid="detail-row-plan_year-error"
                    className="mt-1 text-[11px] font-medium text-ember"
                  >
                    {fieldError.plan_year}
                  </p>
                )}
              </DetailGridRow>
              <DetailGridRow label="Visibility" testId="visibility">
                {(() => {
                  const privateGate = canMakePrivate({
                    detail,
                    currentUserId: auth?.user?.id ?? null,
                  });
                  const privateBlockedReason = privateGate.ok
                    ? null
                    : privateGate.reason;
                  return (
                    <>
                      <select
                        data-testid="commitment-detail-visibility"
                        value={
                          detail.visibility === "private" ? "private" : "team"
                        }
                        disabled={updateMutation.isPending}
                        onChange={(e) => {
                          const next =
                            e.target.value as CommitmentFormVisibility;
                          // Flipping back to Team-visible has no
                          // creator/source/active gates — the BE
                          // accepts it under existing permissions.
                          if (next === "team") {
                            void patch("visibility", { visibility: "team" })
                              .catch(() => {});
                            return;
                          }
                          // Becoming Private — check the gate again
                          // defensively (the option is disabled but
                          // someone could still reach this code).
                          if (privateBlockedReason) return;
                          if (
                            privateReassignmentNeedsConfirmation({
                              currentAssignedTo: detail.assigned_to,
                              currentUserId: auth?.user?.id ?? null,
                            })
                          ) {
                            setPrivateConfirmOpen(true);
                            return;
                          }
                          // Already self-assigned (or unassigned) — no
                          // safety prompt; PATCH straight away. The BE
                          // forces assigned_to=actor on the transition.
                          void patch("visibility", { visibility: "private" })
                            .catch(() => {});
                        }}
                        // Stripped chrome so the trigger lays out flush left
                        // with the inline-edit values above (Account, Tags,
                        // Plan Year). Hover lifts a subtle background, same
                        // affordance as SearchableSelect triggers.
                        className="-ml-1 rounded-md border-0 bg-transparent px-1 py-0.5 text-sm text-text-primary hover:bg-recessed/40 focus:outline-none focus:ring-1 focus:ring-teal disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {COMMITMENT_VISIBILITY_OPTIONS.map((opt) => {
                          const isPrivate = opt.value === "private";
                          const disabled =
                            opt.disabled
                            || (isPrivate && privateBlockedReason !== null);
                          const hint = opt.hint
                            ?? (isPrivate && privateBlockedReason
                              ? PRIVATE_BLOCK_COPY[privateBlockedReason]
                              : undefined);
                          return (
                            <option
                              key={opt.value}
                              value={opt.value}
                              disabled={disabled}
                              data-testid={`commitment-detail-visibility-option-${opt.value}`}
                            >
                              {opt.label}
                              {hint ? ` — ${hint}` : ""}
                            </option>
                          );
                        })}
                      </select>
                      {privateBlockedReason && (
                        <p
                          data-testid="commitment-detail-visibility-helper"
                          className="mt-1 text-[11px] text-text-tertiary"
                        >
                          {PRIVATE_BLOCK_COPY[privateBlockedReason]}
                        </p>
                      )}
                      {fieldError.visibility && (
                        <p
                          role="alert"
                          data-testid="detail-row-visibility-error"
                          className="mt-1 text-[11px] font-medium text-ember"
                        >
                          {fieldError.visibility}
                        </p>
                      )}
                    </>
                  );
                })()}
              </DetailGridRow>
              {(detail.source || detail.source_playbook_application_id || detail.source_touchpoint_id) && (
                <div data-testid="detail-row-source" className="grid grid-cols-[100px_1fr] items-start gap-3">
                  <span className="text-xs text-text-tertiary">Source</span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm text-text-secondary">
                      {detail.source ?? "manual"}
                      {detail.source_playbook_application_id && (
                        detail.source_playbook_id ? (
                          <Link
                            to={`/dashboard/playbooks/${detail.source_playbook_id}/edit`}
                            data-testid="detail-row-source-playbook-link"
                            className="ml-2 inline-flex items-center rounded-md bg-recessed px-1.5 py-0.5 text-[11px] hover:bg-recessed/80 hover:text-teal"
                          >
                            Playbook · {detail.source_playbook_name
                              ?? detail.source_playbook_application_id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span
                            data-testid="detail-row-source-playbook"
                            className="ml-2 rounded-md bg-recessed px-1.5 py-0.5 text-[11px]"
                          >
                            Playbook · {detail.source_playbook_name
                              ?? detail.source_playbook_application_id.slice(0, 8)}
                          </span>
                        )
                      )}
                      {detail.source_touchpoint_id && (
                        <span className="ml-2 rounded-md bg-recessed px-1.5 py-0.5 text-[11px]">
                          Touchpoint · {detail.source_touchpoint_id.slice(0, 8)}
                        </span>
                      )}
                    </span>
                    {detail.source_applied_at && (
                      <span
                        data-testid="detail-row-source-applied-at"
                        className="text-[11px] text-text-tertiary"
                      >
                        Applied {formatDateTime(detail.source_applied_at)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Description card — full-width below the metadata grid.
              Always uses the BlockNote rich editor; legacy plaintext
              descriptions ride in via ``fallbackText`` on first
              render and get linked to a fresh document on first edit. */}
          <CommitmentDescriptionCard
            detail={detail}
            onLinkDocument={(documentId) =>
              patch("description", { description_document_id: documentId })
            }
          />

          {/* Dependency / Subtasks / Attachments — empty states
              with "Add" affordances. Add/remove flows are deferred
              to a later chunk; the BE returns the lists today. */}
          <section
            data-testid="commitment-detail-dependencies"
            className="flex flex-col gap-2"
          >
            <h2 className="text-sm font-semibold text-text-primary">Dependency</h2>
            {detail.dependencies.length === 0 ? (
              <p className="text-xs italic text-text-tertiary">
                No dependencies yet
              </p>
            ) : (
              <ul role="list" className="flex flex-col gap-1 text-sm">
                {detail.dependencies.map((dep) => (
                  <li
                    key={dep.dependency_id}
                    data-testid={`commitment-detail-dependency-${dep.dependency_id}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <Link
                      to={`/dashboard/commitments/${dep.depends_on_commitment_id}`}
                      className="truncate text-text-primary hover:underline"
                    >
                      {dep.title}
                    </Link>
                    <span className="flex items-center gap-2">
                      <CommitmentStatusBadge status={dep.status as CommitmentStatus} />
                      <button
                        type="button"
                        data-testid={`commitment-detail-dependency-remove-${dep.dependency_id}`}
                        aria-label="Remove dependency"
                        disabled={removeDependencyMutation.isPending}
                        onClick={() =>
                          removeDependencyMutation.mutate({
                            commitment_id: detail.commitment_id,
                            dependency_id: dep.dependency_id,
                          })
                        }
                        className="rounded p-0.5 text-text-tertiary hover:text-ember disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        ×
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              data-testid="commitment-detail-add-dependency"
              onClick={() => setDependencyPickerOpen(true)}
              className="inline-flex w-fit items-center gap-1 text-xs font-medium text-teal hover:underline"
            >
              <Plus size={12} aria-hidden="true" />
              Add dependency
            </button>
            {fieldError.dependency && (
              <p
                role="alert"
                data-testid="commitment-detail-dependency-error"
                className="text-[11px] font-medium text-ember"
              >
                {fieldError.dependency}
              </p>
            )}
          </section>

          <section
            data-testid="commitment-detail-subtasks"
            className="flex flex-col gap-2"
          >
            <h2 className="text-sm font-semibold text-text-primary">Subtasks</h2>
            {detail.subtasks.length === 0 ? (
              <p className="text-xs italic text-text-tertiary">No subtasks yet</p>
            ) : (
              <ul role="list" className="flex flex-col gap-1 text-sm">
                {detail.subtasks.map((sub) => (
                  <li
                    key={sub.commitment_id}
                    data-testid={`commitment-detail-subtask-${sub.commitment_id}`}
                    className="flex items-center justify-between gap-2 rounded-md bg-canvas/40 px-3 py-2"
                  >
                    <Link
                      to={`/dashboard/commitments/${sub.commitment_id}`}
                      className="truncate text-text-primary hover:underline"
                    >
                      {sub.title}
                    </Link>
                    <CommitmentStatusBadge status={sub.status} />
                  </li>
                ))}
              </ul>
            )}
            {subtaskOpen ? (
              <form
                data-testid="commitment-detail-subtask-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const title = subtaskDraft.trim();
                  if (!title) return;
                  createMutation.mutate(
                    {
                      title,
                      category_slug: detail.category_slug,
                      client_id: detail.client_id ?? detail.account_id ?? undefined,
                      parent_commitment_id: detail.commitment_id,
                    },
                    {
                      onSuccess: () => {
                        setSubtaskDraft("");
                        setSubtaskOpen(false);
                      },
                      onError: (err) => {
                        setFieldError((p) => ({
                          ...p,
                          subtasks: describeTransitionError(err)
                            ?? "Couldn't create that subtask.",
                        }));
                      },
                    },
                  );
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  data-testid="commitment-detail-subtask-input"
                  autoFocus
                  value={subtaskDraft}
                  onChange={(e) => setSubtaskDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setSubtaskOpen(false);
                      setSubtaskDraft("");
                    }
                  }}
                  placeholder="Subtask title"
                  className="flex-1 rounded-md border border-border-subtle bg-raised px-3 py-2 text-sm text-text-primary"
                />
                <button
                  type="submit"
                  data-testid="commitment-detail-subtask-submit"
                  disabled={createMutation.isPending || !subtaskDraft.trim()}
                  className="rounded-md bg-teal px-3 py-2 text-xs font-semibold text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Add
                </button>
                <button
                  type="button"
                  data-testid="commitment-detail-subtask-cancel"
                  onClick={() => {
                    setSubtaskOpen(false);
                    setSubtaskDraft("");
                  }}
                  className="rounded-md border border-border-subtle px-3 py-2 text-xs text-text-secondary hover:text-text-primary"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                type="button"
                data-testid="commitment-detail-add-subtask"
                onClick={() => setSubtaskOpen(true)}
                className="inline-flex w-fit items-center gap-1 text-xs font-medium text-teal hover:underline"
              >
                <Plus size={12} aria-hidden="true" />
                Add subtask
              </button>
            )}
            {fieldError.subtasks && (
              <p
                role="alert"
                data-testid="detail-row-subtasks-error"
                className="text-[11px] font-medium text-ember"
              >
                {fieldError.subtasks}
              </p>
            )}
          </section>

          <CommitmentAttachmentsPanel
            commitmentId={detail.commitment_id}
            accountId={detail.client_id ?? detail.account_id ?? null}
            planYearId={detail.plan_year_id ?? null}
          />

          {detail.status === "cancelled" && detail.cancellation_reason && (
            <section
              data-testid="commitment-detail-cancellation-reason"
              className="rounded-md border border-ember/40 bg-ember-tint/40 p-4 text-sm"
            >
              <h2 className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
                Cancellation reason
              </h2>
              <p className="mt-2 whitespace-pre-wrap leading-relaxed text-text-primary">
                {detail.cancellation_reason}
              </p>
            </section>
          )}

          {/* Inline reason prompts for cancellation / block flows. */}
          {pendingCancel && (
            <div className="flex flex-col gap-2 rounded-md bg-recessed p-3">
              <label
                htmlFor="commitment-detail-cancellation-reason"
                className="text-xs font-medium text-text-secondary"
              >
                Cancellation reason
              </label>
              <input
                type="text"
                id="commitment-detail-cancellation-reason"
                data-testid="commitment-detail-cancellation-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="rounded-md border border-border-subtle bg-raised px-3 py-2 text-sm text-text-primary"
              />
              {cancelReasonError && (
                <span className="text-[11px] font-medium text-ember">
                  {cancelReasonError}
                </span>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPendingCancel(false);
                    setCancelReason("");
                    setCancelReasonError(null);
                  }}
                  className="rounded-md px-2.5 py-1 text-xs text-text-secondary hover:text-text-primary"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  data-testid="commitment-detail-cancellation-submit"
                  onClick={submitCancellation}
                  className="rounded-md bg-ember px-2.5 py-1 text-xs font-medium text-text-inverse hover:opacity-90"
                >
                  Cancel Commitment
                </button>
              </div>
            </div>
          )}

          {transitionError && !fieldError.status && (
            <div
              role="alert"
              data-testid="commitment-detail-transition-error"
              className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
            >
              {transitionError}
            </div>
          )}

          {pendingDelete && (
            <div
              role="alertdialog"
              aria-labelledby="commitment-detail-delete-title"
              className="rounded-md border border-ember-tint bg-recessed p-4"
            >
              <h2
                id="commitment-detail-delete-title"
                className="text-sm font-semibold text-text-primary"
              >
                Delete this Commitment?
              </h2>
              <p className="mt-1 text-xs text-text-secondary">
                This soft-deletes the row. Status history and audit logs
                are preserved.
              </p>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPendingDelete(false)}
                  className="rounded-md px-2.5 py-1 text-xs text-text-secondary hover:text-text-primary"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  data-testid="commitment-detail-delete-confirm"
                  onClick={() =>
                    deleteMutation.mutate(undefined, {
                      onSuccess: () => onDeleted(),
                    })
                  }
                  disabled={deleteMutation.isPending}
                  className="rounded-md bg-ember px-2.5 py-1 text-xs font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </article>
      </div>

        {/* Right rail — Comments / Activity. Read-only for now;
            comments are a future chunk. */}
        <CommitmentDetailRightPanel detail={detail} />
      </div>

      <PrivateVisibilityConfirmModal
        open={privateConfirmOpen}
        currentAssigneeName={
          detail.assigned_to_display_name
          || detail.assigned_to_email
          || "another teammate"
        }
        isPending={updateMutation.isPending}
        onCancel={() => setPrivateConfirmOpen(false)}
        onConfirm={() => {
          setPrivateConfirmOpen(false);
          void patch("visibility", { visibility: "private" })
            .catch(() => {});
        }}
      />

      {/* Only mount when open so the inner ``useCommitmentListQuery``
          doesn't fire (and require a QueryClient) on every detail
          render. */}
      <CreateTagModal
        open={createTagOpen}
        isPending={createTagMutation.isPending || tagMutation.isPending}
        error={
          createTagMutation.isError
            ? (createTagMutation.error as Error).message
            : null
        }
        onCancel={() => setCreateTagOpen(false)}
        onCreate={({ display_name, is_regulatory }) => {
          createTagMutation.mutate(
            { display_name, is_regulatory },
            {
              onSuccess: (tag) => {
                // Auto-attach the new tag to the current
                // Commitment so the user doesn't have to reopen
                // the picker. Idempotent on the BE.
                tagMutation.mutate(
                  {
                    commitment_id: detail.commitment_id,
                    tag_id: tag.id,
                  },
                  {
                    onSuccess: () => setCreateTagOpen(false),
                    onError: () => setCreateTagOpen(false),
                  },
                );
              },
            },
          );
        }}
      />

      {dependencyPickerOpen && (
        <DependencyPickerModal
          open
          selfCommitmentId={detail.commitment_id}
          onCancel={() => setDependencyPickerOpen(false)}
          onPick={(id) => {
            addDependencyMutation.mutate(
              {
                commitment_id: detail.commitment_id,
                depends_on_commitment_id: id,
              },
              {
                onSuccess: () => {
                  setDependencyPickerOpen(false);
                  setFieldError((prev) => ({ ...prev, dependency: null }));
                },
                onError: (err) => {
                  setFieldError((prev) => ({
                    ...prev,
                    dependency: describeTransitionError(err)
                      ?? "Couldn't add that dependency.",
                  }));
                },
              },
            );
          }}
        />
      )}

      <ConfirmDialog
        open={accountChangeConfirm !== null}
        title="Change Account on this Commitment?"
        body={
          accountChangeConfirm
            ? `This Commitment has ${accountChangeConfirm.attachmentCount} `
              + `attachment${accountChangeConfirm.attachmentCount === 1 ? "" : "s"} `
              + `linked to the previous Account's documents. Changing `
              + `the Account won't move them — you'll need to unlink `
              + `and relink them under the new Account.`
            : ""
        }
        confirmLabel="Change Account"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          const next = accountChangeConfirm;
          setAccountChangeConfirm(null);
          if (!next) return;
          void patch("account", { client_id: next.nextAccountId })
            .catch(() => {});
        }}
        onCancel={() => setAccountChangeConfirm(null)}
        testId="commitment-account-change-confirm"
      />
    </div>
  );
}


function DetailGridRow({
  label, testId, children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={`detail-row-${testId}`}
      className="grid grid-cols-[100px_1fr] items-start gap-3"
    >
      <span className="pt-1.5 text-xs text-text-tertiary">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}


function BlockedToggle({
  isBlocked, pendingBlock, onBlockClick, onUnblockClick, pending,
}: {
  isBlocked: boolean;
  pendingBlock: boolean;
  onBlockClick: () => void;
  onUnblockClick: () => void;
  pending: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isBlocked}
      data-testid={isBlocked ? "commitment-detail-unblock" : "commitment-detail-block"}
      disabled={pending || pendingBlock}
      onClick={isBlocked ? onUnblockClick : onBlockClick}
      className={[
        "relative inline-flex h-5 w-8 items-center rounded-full transition-fast ease-out",
        isBlocked ? "bg-gold" : "bg-recessed",
        "disabled:cursor-not-allowed disabled:opacity-60",
      ].join(" ")}
    >
      <span
        className={[
          "inline-block h-3.5 w-3.5 transform rounded-full bg-raised shadow transition-fast ease-out",
          isBlocked ? "translate-x-3.5" : "translate-x-0.5",
        ].join(" ")}
      />
    </button>
  );
}


function ComplexityChip({
  complexity,
}: { complexity: number | null | undefined }) {
  const letter = complexityLetter(complexity);
  if (!letter) {
    return <span className="text-text-tertiary">Unsized</span>;
  }
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-sage-tint px-2 py-0.5 text-[11px] font-semibold text-teal">
      {letter}
    </span>
  );
}


function formatPlanYearRange(
  startIso: string | null, endIso: string | null,
): string {
  function fmt(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  }
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (a && b) return `${a} – ${b}`;
  if (a) return a;
  if (b) return b;
  return "—";
}


function priorityLabel(p: string): string {
  switch (p) {
    case "high": return "High";
    case "medium": return "Medium";
    case "low": return "Low";
    default: return p;
  }
}


function complexityLetter(complexity: number | null | undefined): string | null {
  switch (complexity) {
    case 1: return "S";
    case 2: return "M";
    case 3: return "L";
    case 5: return "XL";
    default: return null;
  }
}


function AssigneeAvatar({
  initials, display,
}: { initials: string; display: string }) {
  if (display === "Unassigned") {
    return <span className="italic text-text-tertiary">Unassigned</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sage-tint text-[10px] font-semibold text-teal"
      >
        {initials}
      </span>
      <span>{display}</span>
    </span>
  );
}


function initialsOf(name: string): string {
  const cleaned = name.trim();
  if (cleaned === "" || cleaned === "Unassigned") return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return cleaned.slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}


function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // ``datetime-local`` wants ``YYYY-MM-DDTHH:mm`` in the user's
  // local timezone; build it manually to avoid timezone-shift
  // surprises from ``toISOString``.
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


function CommitmentDescriptionCard({
  detail, onLinkDocument,
}: {
  detail: import("./types").CommitmentDetail;
  onLinkDocument: (documentId: string) => Promise<void>;
}) {
  return (
    <section
      data-testid="commitment-detail-description"
      data-mode="rich"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
          Description
        </span>
        <Sparkles
          size={12} aria-hidden="true"
          className="text-text-tertiary"
        />
      </header>
      <BlockNoteEditor
        documentId={detail.description_document_id ?? null}
        documentKind="commitment_description"
        fallbackText={detail.description ?? null}
        onCreated={(documentId) => {
          void onLinkDocument(documentId);
        }}
      />
    </section>
  );
}


